import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const DebugReset = () => {
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [tokenParsed, setTokenParsed] = useState<boolean>(false);
  const [sessionChecked, setSessionChecked] = useState<boolean>(false);

  useEffect(() => {
    const runDebugChecks = async () => {
      try {
        // 1. Capture current URL information
        const currentUrl = window.location.href;
        const urlObj = new URL(currentUrl);
        const hash = urlObj.hash;
        const rawHash = hash.startsWith('#') ? hash.substring(1) : hash;
        const urlParams = new URLSearchParams(urlObj.search);
        const hashParams = new URLSearchParams(rawHash);
        
        // 2. Extract and log token information
        const accessToken = hashParams.get('access_token');
        const tokenType = hashParams.get('type') || urlParams.get('type');
        const refreshToken = hashParams.get('refresh_token');
        
        // Store basic URL info
        setDebugInfo({
          url: currentUrl,
          pathname: urlObj.pathname,
          origin: urlObj.origin,
          accessTokenPresent: !!accessToken,
          accessTokenLength: accessToken ? accessToken.length : 0,
          tokenType,
          refreshTokenPresent: !!refreshToken,
          hashLength: hash ? hash.length : 0,
          hashParamCount: Array.from(hashParams.entries()).length,
          searchParamCount: Array.from(urlParams.entries()).length
        });
        
        // 3. Try to validate the token if present
        if (accessToken) {
          try {
            // Attempt to set session with the token
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || ''
            });
            
            setTokenParsed(true);
            
            // Update debug info with token validation results
            setDebugInfo(prev => ({
              ...prev,
              tokenValidationError: error ? error.message : null,
              tokenValidationSuccess: !error,
              sessionData: data ? 'Session data available' : 'No session data',
              setSessionResponse: {
                error: error ? { message: error.message, status: error.status } : null,
                success: !error
              }
            }));
          } catch (tokenError: any) {
            setDebugInfo(prev => ({
              ...prev,
              tokenValidationError: tokenError.message,
              tokenValidationErrorStack: tokenError.stack,
              tokenValidationSuccess: false
            }));
          }
        }
        
        // 4. Check if there's an existing session
        try {
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          
          setSessionChecked(true);
          
          setDebugInfo(prev => ({
            ...prev,
            sessionExists: !!sessionData?.session,
            sessionError: sessionError ? sessionError.message : null,
            sessionUserId: sessionData?.session?.user?.id || null
          }));
        } catch (sessionCheckError: any) {
          setDebugInfo(prev => ({
            ...prev,
            sessionCheckError: sessionCheckError.message
          }));
        }
      } catch (error: any) {
        setDebugInfo(prev => ({
          ...prev,
          mainError: error.message,
          mainErrorStack: error.stack
        }));
      }
    };
    
    runDebugChecks();
  }, []);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">Password Reset Debug</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">URL Information</h2>
          <div className="space-y-2">
            <p><strong>Current URL:</strong> {debugInfo.url}</p>
            <p><strong>Origin:</strong> {debugInfo.origin}</p>
            <p><strong>Pathname:</strong> {debugInfo.pathname}</p>
            <p><strong>Hash length:</strong> {debugInfo.hashLength}</p>
            <p><strong>Hash params:</strong> {debugInfo.hashParamCount}</p>
            <p><strong>Search params:</strong> {debugInfo.searchParamCount}</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">Token Information</h2>
          <div className="space-y-2">
            <p><strong>Access token present:</strong> {debugInfo.accessTokenPresent ? 'Yes' : 'No'}</p>
            {debugInfo.accessTokenPresent && (
              <p><strong>Access token length:</strong> {debugInfo.accessTokenLength}</p>
            )}
            <p><strong>Token type:</strong> {debugInfo.tokenType || 'Not specified'}</p>
            <p><strong>Refresh token present:</strong> {debugInfo.refreshTokenPresent ? 'Yes' : 'No'}</p>
            <p><strong>Token validation:</strong> {tokenParsed ? (debugInfo.tokenValidationSuccess ? 'Success' : 'Failed') : 'Not attempted'}</p>
            {debugInfo.tokenValidationError && (
              <p><strong>Validation error:</strong> {debugInfo.tokenValidationError}</p>
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">Session Information</h2>
          <div className="space-y-2">
            <p><strong>Session check:</strong> {sessionChecked ? 'Completed' : 'Pending'}</p>
            <p><strong>Session exists:</strong> {debugInfo.sessionExists ? 'Yes' : 'No'}</p>
            {debugInfo.sessionUserId && (
              <p><strong>User ID:</strong> {debugInfo.sessionUserId}</p>
            )}
            {debugInfo.sessionError && (
              <p><strong>Session error:</strong> {debugInfo.sessionError}</p>
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">Actions</h2>
          <div className="space-y-4">
            <button 
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
              onClick={async () => {
                try {
                  const hashParams = new URLSearchParams(
                    window.location.hash.startsWith('#') 
                      ? window.location.hash.substring(1) 
                      : window.location.hash
                  );
                  const accessToken = hashParams.get('access_token');
                  
                  if (accessToken) {
                    const { error } = await supabase.auth.setSession({
                      access_token: accessToken,
                      refresh_token: hashParams.get('refresh_token') || ''
                    });
                    
                    setDebugInfo(prev => ({
                      ...prev,
                      manualSetSessionResult: error ? `Error: ${error.message}` : 'Success'
                    }));
                  } else {
                    setDebugInfo(prev => ({
                      ...prev,
                      manualSetSessionResult: 'No access token found'
                    }));
                  }
                } catch (e: any) {
                  setDebugInfo(prev => ({
                    ...prev,
                    manualSetSessionResult: `Exception: ${e.message}`
                  }));
                }
              }}
            >
              Try Setting Session Manually
            </button>
            
            <div>
              {debugInfo.manualSetSessionResult && (
                <p><strong>Result:</strong> {debugInfo.manualSetSessionResult}</p>
              )}
            </div>
            
            <button 
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
              onClick={() => {
                window.location.href = '/reset-password';
              }}
            >
              Go to Regular Reset Page
            </button>
          </div>
        </div>
      </div>
      
      <div className="mt-6 bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-2">Full Debug Data</h2>
        <pre className="bg-gray-100 p-4 rounded overflow-auto text-xs">
          {JSON.stringify(debugInfo, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default DebugReset;
